import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

// Feature icon map. Extend as needed — unknown values fall back to a
// generic bullet. Kept inline so admins can pick from a stable set
// without an extra API round-trip.
const FEATURE_ICONS = {
  message: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  ),
  users: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  ),
  automation: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  ),
  analytics: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6m4 6v-10m4 10V9M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
  ),
  shield: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  ),
  bolt: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  ),
  check: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  ),
};

const FeatureIcon = ({ name, color }) => (
  <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke={color} viewBox="0 0 24 24" strokeWidth={2}>
    {FEATURE_ICONS[name] || FEATURE_ICONS.check}
  </svg>
);

const Login = () => {
  const { branding } = useBranding();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for session expiration in URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('session_expired') === 'true') {
      setSessionExpired(true);
      // Clear the URL parameter after reading it
      navigate('/login', { replace: true });
    }
  }, [location, navigate]);

  // Update <title> and <meta description> from tenant SEO fields so
  // crawlers that execute JS see per-tenant metadata. The BrandingContext
  // already updates <title> from app_name for authenticated pages; here
  // we override with the SEO-specific values on the login route.
  useEffect(() => {
    const appName = branding.app_name || 'SnapText';
    const title = branding.login_seo_title || `${appName} — Sign in`;
    document.title = title;
    const desc = branding.login_seo_description
      || branding.login_subheadline
      || 'Professional messaging platform for businesses.';
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', desc);
  }, [branding.login_seo_title, branding.login_seo_description, branding.login_subheadline, branding.app_name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login({ email, password });

      if (result.success) {
        navigate('/');
      } else if (result.requires2FA) {
        navigate('/verify-2fa', {
          state: {
            userId: result.userId,
            email: result.email,
            password: result.password
          }
        });
      } else {
        let errorMessage = result.error || 'Login failed. Please try again.';
        if (result.remainingAttempts !== undefined && result.remainingAttempts > 0) {
          errorMessage += ` (${result.remainingAttempts} attempt${result.remainingAttempts !== 1 ? 's' : ''} remaining)`;
        }
        setError(errorMessage);
        setLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const primary = branding.primary_color || '#3B82F6';
  const hasMarketing = !!(branding.login_headline || (branding.login_features && branding.login_features.length));

  // Show the "Learn more about SnapText" button on any Snapcom-owned
  // host (texting.snapcom.com in prod, devtext.snapcom.com in dev,
  // localhost during local dev). Whitelabel tenants live on their own
  // custom domain and don't get a Snapcom-branded marketing link.
  const showSnapTextMarketing = typeof window !== 'undefined' && (
    /(^|\.)snapcom\.com$/i.test(window.location.hostname) ||
    window.location.hostname === 'localhost'
  );

  const marketingLinkBlock = showSnapTextMarketing ? (
    <div className="mt-5 pt-5 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 text-sm">
      <span className="text-gray-500">New here?</span>
      <Link
        to="/features"
        className="inline-flex items-center gap-1.5 font-semibold hover:underline"
        style={{ color: primary }}
      >
        See what SnapText can do
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </Link>
      <span className="text-gray-300 hidden sm:inline">·</span>
      <Link to="/onepager" className="text-gray-500 hover:text-gray-700 font-medium">
        Read the one-pager
      </Link>
    </div>
  ) : null;

  // Landing-mode form: proper labels, prominent Sign in, secondary
  // links below. Matches the reference card style — white surface,
  // generous spacing, no cramped combo input group.
  const landingForm = (
    <div className="w-full max-w-md">
      {/* Logo + app name pill — anchors the form side and gives mobile
          (where the left panel is hidden) a clear brand identity above
          the Sign-in heading. */}
      <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-900 text-white mb-6">
        {branding.logo_url && (
          <img src={branding.logo_url} alt="" className="h-6 w-auto" />
        )}
        <span className="text-sm font-semibold tracking-tight">{branding.app_name || 'SnapText'}</span>
      </div>

      <h2 className="text-2xl font-bold text-gray-900">Sign in</h2>
      <p className="mt-1 text-sm text-gray-500">
        {branding.app_name ? `Access your ${branding.app_name} account.` : 'Access your account.'}
      </p>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        {sessionExpired && (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
            Your session has expired. Please sign in again.
          </div>
        )}
        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-1.5">Email</label>
          <input
            id="email" name="email" type="email" autoComplete="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-semibold text-gray-800 mb-1.5">Password</label>
          <input
            id="password" name="password" type="password" autoComplete="current-password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>

        <button
          type="submit" disabled={loading}
          className="w-full py-2.5 px-4 rounded-lg text-white text-sm font-semibold shadow-sm hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
          style={{ backgroundColor: primary }}
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="text-sm text-gray-500">
          <span>Forgot your password? </span>
          <Link to="/forgot-password" className="font-semibold hover:underline" style={{ color: primary }}>
            Reset it
          </Link>
        </div>
        {marketingLinkBlock}
      </form>
    </div>
  );

  // Classic-mode form (unchanged behavior for tenants without landing content).
  const classicForm = (
    <div className="w-full max-w-md">
      <div className="flex justify-center mb-4">
        {branding.logo_url && (
          <img src={branding.logo_url} alt={branding.app_name || 'Logo'} className="h-14" />
        )}
      </div>
      <h2 className="mt-6 text-center text-4xl font-extrabold" style={{ color: primary }}>
        {branding.app_name || 'SnapText'}
      </h2>
      {branding.tagline && (
        <p className="mt-2 text-center text-sm text-gray-600">{branding.tagline}</p>
      )}
      <h3 className="mt-2 text-center text-2xl font-bold text-gray-900">
        Sign in to your account
      </h3>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        {sessionExpired && (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
            <p className="text-sm text-yellow-800 font-medium">Your session has expired. Please log in again.</p>
          </div>
        )}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        <div className="rounded-md shadow-sm -space-y-px">
          <div>
            <label htmlFor="email-classic" className="sr-only">Email address</label>
            <input
              id="email-classic" name="email" type="email" autoComplete="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="Email address"
            />
          </div>
          <div>
            <label htmlFor="password-classic" className="sr-only">Password</label>
            <input
              id="password-classic" name="password" type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="Password"
            />
          </div>
        </div>

        <button
          type="submit" disabled={loading}
          className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
          style={{ backgroundColor: primary }}
        >
          {loading ? 'Signing in...' : 'Sign in'}
        </button>

        <div className="text-sm text-center">
          <Link to="/forgot-password" className="font-medium hover:opacity-80" style={{ color: primary }}>
            Forgot your password?
          </Link>
        </div>
        {marketingLinkBlock}
      </form>
    </div>
  );

  // No marketing content configured → classic centered form.
  if (!hasMarketing) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8"
        style={branding.login_background_url ? {
          backgroundImage: `url(${branding.login_background_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {}}
      >
        <div className={`max-w-md w-full space-y-8 ${branding.login_background_url ? 'bg-white/95 p-8 rounded-lg shadow-xl' : ''}`}>
          {classicForm}
        </div>
      </div>
    );
  }

  // Landing mode — a contained card sitting on a dark page. Left panel
  // is a light neutral surface with feature *cards*, right panel is the
  // login form on white. Mobile drops the marketing panel entirely.
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 sm:p-6 lg:p-10">
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col lg:flex-row">
        {/* Marketing panel — desktop only. Logo moved to the form
            column so this side is pure headline + features + CTA. */}
        <aside className="hidden lg:flex lg:w-1/2 bg-slate-50 border-r border-slate-200 p-10 xl:p-14">
          <div className="w-full max-w-lg">
            <h1 className="text-3xl xl:text-[2rem] font-extrabold text-slate-900 leading-tight" style={{ textWrap: 'balance' }}>
              {branding.login_headline}
            </h1>
            {branding.login_subheadline && (
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                {branding.login_subheadline}
              </p>
            )}

            {branding.login_features && branding.login_features.length > 0 && (
              <ul className="mt-8 space-y-3">
                {branding.login_features.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 shadow-sm p-4"
                  >
                    <span className="flex items-center justify-center flex-shrink-0 mt-0.5" style={{ color: primary }}>
                      <FeatureIcon name={f.icon} color="currentColor" />
                    </span>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{f.title}</div>
                      {f.description && (
                        <div className="text-sm text-slate-500 leading-snug mt-0.5">{f.description}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {branding.login_cta_text && branding.login_cta_url && (
              <p className="mt-8 text-sm text-slate-600 leading-relaxed">
                Already have {branding.app_name || 'an account'}? Contact your administrator for access. New to {branding.app_name || 'us'}?{' '}
                <a
                  href={branding.login_cta_url}
                  className="font-semibold hover:underline inline-flex items-center gap-0.5"
                  style={{ color: primary }}
                >
                  {branding.login_cta_text}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
              </p>
            )}
          </div>
        </aside>

        {/* Form panel */}
        <main className="flex-1 flex items-center justify-center bg-white p-8 sm:p-10 lg:p-14">
          {landingForm}
        </main>
      </div>
    </div>
  );
};

export default Login;
